<template>
  <a-modal
    :title="$t('warehouse.select_box')"
    v-model:visible="state.visible"
    :centered="true"
    :mask-closable="false"
    width="auto"
    @cancel="close"
    @ok="handleConfirm"
  >
    <a-row class="pb-3" :gutter="32">
      <a-col>
        {{ $t("warehouse.shipment_name") }}: {{ props.shipment?.name || "-" }}
      </a-col>
      <a-col>
        {{ $t("warehouse.se_shipment_number") }}: {{ props.shipment?.seShipmentNo || "-" }}
      </a-col>
    </a-row>
    <a-table
      size="small"
      :columns="columns"
      :data-source="state.tableData.tableList"
      :pagination="false"
      :scroll="{ x: 500 }"
      :row-key="(record) => record.whboxId"
      :loading="state.tableData.loading"
      :row-selection="{
        selectedRowKeys: state.selectedRowKeys,
        onChange: handleRowSelectionChange,
        getCheckboxProps: (record) => ({ disabled: !record.isOperability })
      }"
    >
      <template #boxNo="{ record }">
        <span class="pr-2">{{ record.boxNo }}</span>
        <a-tag v-if="record.isChangeLabel" color="orange">{{ $t('warehouse.replaced_box_label') }}</a-tag>
      </template>
      <template #status="{ record }">
        <template v-if="!record.isOperability">
          <small v-if="!!record.whBoxServiceType">{{ $t('common.reason') }}: {{ $t('warehouse.occupy_by_p0', [$t($enumLangkey("whBoxServiceType", record.whBoxServiceType))]) }}</small>
          <small v-else-if="!!record.containerBoxStatus">{{ $t('common.reason') }}: {{ $t('warehouse.box_status_is_p0', [$t($enumLangkey("containerBoxStatus", record.containerBoxStatus))]) }}</small>
        </template>
      </template>
      <template #footer>
        <a-popover placement="top" :title="$t('warehouse.selected_box_details')">
          <template #content>
            <a-row :gutter="[8,8]" style="max-width:800px">
              <a-col v-for="(item, index) in state.selectedRows" :key="index">
                <a-tag color="default">{{ item.boxNo }}</a-tag>
              </a-col>
            </a-row>
          </template>
          <span>
            {{
              $t(
                "warehouse.p0_boxes_have_been_selected_the_total_shipment_is_p1_boxes",
                [state.selectedRowKeys.length, state.tableData.pageData.totalCount]
              )
            }}
          </span>
        </a-popover>
      </template>
    </a-table>
    <CPager
      class="text-center mt-3"
      :page-data="state.tableData.pageData"
      @handlePage="handlePage"
    ></CPager>
  </a-modal>
</template>

<script>
import { defineComponent, reactive, watch } from 'vue';
import { Button, Col, Modal, Row, Table, Tag, Popover, message } from 'ant-design-vue';
import CPager from "@/views/components/CPager.vue";
import { useI18n } from "vue-i18n/index";
import { getCreateLabelShipmentDetailPagedList } from "@/api/modules/transit/labelChange.js";

export default defineComponent({
  emits: ['update:visible', 'confirm'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    shipment: {
      type: Object,
      default: () => ({})
    },
  },
  components: {
    AModal: Modal,
    AButton: Button,
    ARow: Row,
    ACol: Col,
    ATable: Table,
    ATag: Tag,
    APopover: Popover,
    CPager,
  },
  setup (props, { emit }) {
    const vueI18n = useI18n({ useScope: "global" });

    const state = reactive({
      visible: false,
      selectedRows: [],
      selectedRowKeys: [],
      tableData: {
        tableList: [],
        loading: false,
        pageData: {
          currentIndex: 1,
          skipCount: 0,
          totalCount: 0,
          maxResultCount: 10,
        },
      },
    });

    const columns = [
      {
        dataIndex: "canScanedBoxNo",
        width: 200,
        title: () => vueI18n.t("warehouse.scannable_box_no"),
      },
      {
        width: 200,
        title: () => vueI18n.t("warehouse.box_id"),
        slots: {
          customRender: "boxNo",
        },
      },
      {
        dataIndex: "status",
        width: 150,
        title: () => "",
        slots: {
          customRender: "status",
        },
      },
    ];

    const handleRowSelectionChange = (selectedRowKeys, selectedRows) => {
      state.selectedRowKeys = selectedRowKeys;
      
      selectedRows.forEach((record) => {
        let selectedRowsKeyList = state.selectedRows.map(item => item.whboxId);
        if (selectedRowKeys.includes(record.whboxId) && !selectedRowsKeyList.includes(record.whboxId)) {
          state.selectedRows.push(record);
        }
      });

      state.selectedRows = state.selectedRows.filter((record) => {
        return selectedRowKeys.includes(record.whboxId);
      });
    };

    const getPageList = () => {
      state.tableData.loading = true;
      const data = Object.assign({}, state.tableData.pageData, {
        shipmentId: props.shipment.id,
      });
      getCreateLabelShipmentDetailPagedList(data)
        .then((res) => {
          let { items = [], totalCount = 0 } = res.result;
          state.tableData.pageData.totalCount = parseInt(totalCount);
          state.tableData.tableList = items;
        })
        .catch(() => {})
        .finally(() => {
          state.tableData.loading = false;
        });
    };

    const handlePage = (pageData) => {
      state.tableData.pageData.skipCount = pageData.skipCount;
      state.tableData.pageData.maxResultCount = pageData.maxResultCount;
      getPageList();
    };

    const handleInitPage = () => {
      state.tableData.pageData.skipCount = 0;
      state.tableData.pageData.currentIndex = 1;
      getPageList();
    };
    
    const handleResetShipments = () => {
      state.tableData.tableList = [];
      state.tableData.pageData.skipCount = 0;
      state.tableData.pageData.totalCount = 0;
      state.tableData.pageData.currentIndex = 1;
    };

    const handleConfirm = () => {
      if (!state.selectedRowKeys.length) {
        message.error(vueI18n.t("warehouse.please_select_box"));
        return;
      }
      emit('confirm', JSON.parse(JSON.stringify(state.selectedRows)));
      close();
    }

    const close = () => {
      emit('update:visible', false);
    }

    watch(() => props.visible, (visible) => {
      if (typeof visible === 'boolean') {
        state.visible = visible;
        handleResetShipments();
        if (visible && !!props.shipment) {
          state.selectedRowKeys = [];
          state.selectedRows = [];
          handleInitPage();
        }
      }
    }, { immediate: true, deep: true });

    return {
      state,
      props,
      columns,
      handleRowSelectionChange,
      handlePage,
      handleConfirm,
      close,
    }
  }
})
</script>

<style scoped>

</style>